import { toPairs, reduce, map, prop } from 'ramda'

import type {
  TimeRestrictions,
  EnabledTimeRestrictions,
  ArrangeFoodModes,
  Params,
  ParamsPOST,
  TimeRestrictionItemStrict,
} from 'app/dataTypes/OrgSettingsTimeRestrictions.types'

import { foodModes as fm, type FoodMode } from 'app/dataTypes/foodModes'

import type { Request } from '../../index'

type TimeRestrictionApiItem =
  | {
      time: string
      day_mode: 'before' | 'today'
      wday: number | null
    }
  | {
      time: string
      day_mode: 'next_week'
      wday: number // 0 - Воскресенье, 1 - Понедельник, ...
    }
  | {
      time: null
      day_mode: null
      wday: null
    }

type TimeRestrictionApiGroup = {
  '1': TimeRestrictionApiItem
  '2': TimeRestrictionApiItem
  '3': TimeRestrictionApiItem
}

type Result = {
  enabled: boolean
  restrictions: {
    afternoon: TimeRestrictionApiGroup
    breakfast: TimeRestrictionApiGroup
    dinner: TimeRestrictionApiGroup
  }
  result: 'success'
}

type UrlParams = {
  orgId: number | string
}

const apiFoodKey = (key: FoodMode): FoodMode => key

// GET

export const convertParams = (params: Params): [null, UrlParams] => {
  return [null, params]
}

const arrangeFoodModes: ArrangeFoodModes = [fm.Breakfast, fm.Dinner, fm.Afternoon]

export const convertResult = (data: Result): [TimeRestrictions, EnabledTimeRestrictions] => {
  const { enabled, restrictions } = data
  const timeRestrictions = reduce(
    (acc, key) => {
      const groupPairs = toPairs(restrictions[apiFoodKey(key)])
      map(([index, { time, day_mode: dayMode, wday }]) => {
        const groupIndex = parseInt(index, 10) - 1
        const isTimeValid = /^(20|21|22|23|[0-1][0-9]):[0-5][0-9]$/.test(time)
        if (!isTimeValid) {
          console.warn(`Невалидный формат времени пришел из get cafeteria_orders_time_restrictions`)
        }
        const restriction = isTimeValid
          ? { time, dayMode, wday }
          : { time: null, dayMode: null, wday: null }
        acc[groupIndex] =
          acc[groupIndex] instanceof Array ? [...acc[groupIndex], restriction] : [restriction]
      }, groupPairs)
      return acc
    },
    [] as TimeRestrictions,
    arrangeFoodModes,
  )

  return [timeRestrictions, enabled]
}

export const GET_CAFETERIA_ORDERS_TIME_RESTRICTIONS: Request<
  Params,
  [TimeRestrictions, EnabledTimeRestrictions],
  UrlParams
> = ['food/schools/{orgId}/cafeteria_orders_time_restrictions', 'get', ['data']]

// POST

type TimeRestrictionApiGroupPOST = {
  '1': TimeRestrictionApiItem
  '2': TimeRestrictionApiItem
  '3': TimeRestrictionApiItem
}

type ParamsApiPOST = {
  schools_ids: Array<string> | Array<number>
  enabled: boolean
  restrictions: {
    afternoon: TimeRestrictionApiGroupPOST
    breakfast: TimeRestrictionApiGroupPOST
    dinner: TimeRestrictionApiGroupPOST
  }
}

const convertTime = ({
  time,
  dayMode,
  wday,
}: TimeRestrictionItemStrict): TimeRestrictionApiItem => {
  if (dayMode === 'next_week') {
    return {
      day_mode: dayMode,
      time,
      wday,
    }
  }
  return {
    day_mode: dayMode,
    time,
    wday,
  }
}

export const convertParamsPOST = (params: ParamsPOST): [ParamsApiPOST, null] => {
  const { orgIds, enabled, restrictions: componentRestrictions } = params
  const restrictions = arrangeFoodModes.reduce(
    (acc, key, foodIndex) => {
      const foodKey = apiFoodKey(key)
      const targetGroups = acc[foodKey] || {}
      const newGroups = componentRestrictions.reduce((accGroups, group, groupIndex) => {
        const foodItem = prop(foodIndex, group)
        accGroups = {
          ...accGroups,
          [String(groupIndex + 1)]: convertTime(foodItem),
        }
        return accGroups
      }, {} as TimeRestrictionApiGroupPOST)

      acc[foodKey] = {
        ...targetGroups,
        ...newGroups,
      }
      return acc
    },
    {} as ParamsApiPOST['restrictions'],
  )

  return [{ enabled, restrictions, schools_ids: orgIds }, null]
}

export const POST_CAFETERIA_ORDERS_TIME_RESTRICTIONS: Request<ParamsPOST, 'success', void> = [
  'food/schools/cafeteria_orders_time_restrictions',
  'post',
  ['data', 'result'],
]
